import { useEffect, useRef, useState } from 'react';
import { Identifier, useResourceDefinitions } from 'react-admin';
import { useLocation } from 'react-router-dom';
import { notifyManager, useQueryClient, QueryClient } from 'react-query';

import { AppLocation } from './AppLocationContext';

import {
  ResourceLocationInfo,
  resolveResourceLocationInfo,
} from './resolveResourceLocationInfo';

const buildResourceLocationInfoPath = ({
  resource,
  type,
}: ResourceLocationInfo): string => `${resource}${type === 'list' ? '' : `.${type}`}`;

export const useResourceAppLocation = (): AppLocation | null => {
  const { pathname } = useLocation();
  const resources = useResourceDefinitions();

  // Since this can be null at mount, don't memoize it
  const resourceLocationInfo = resolveResourceLocationInfo(
    pathname,
    Object.values(resources)
  );

  // Only way I found to be notified when the data is actually available for the query
  const record = useRecordForCurrentRoute(
    resourceLocationInfo?.resource,
    resourceLocationInfo?.recordId
  );

  if (pathname === '/') {
    return {
      path: '',
      values: {},
    };
  }

  if (!resourceLocationInfo) {
    return null;
  }

  return {
    path: buildResourceLocationInfoPath(resourceLocationInfo),
    values: { record },
  };
};

const getRecordFromQueryCache = (
  queryClient: QueryClient,
  resource: string,
  id: Identifier
) => {
  const queriesData = queryClient.getQueriesData<any>([resource, 'getOne', { id }]);

  return queriesData.length > 0 ? queriesData[0][1] : null;
};

const useRecordForCurrentRoute = (resource: string, id: Identifier) => {
  const queryClient = useQueryClient();
  const mountedRef = useRef(false);
  const recordRef = useRef(getRecordFromQueryCache(queryClient, resource, id));

  const [record, setRecord] = useState<any>(recordRef.current);

  useEffect(() => {
    mountedRef.current = true;
    recordRef.current = getRecordFromQueryCache(queryClient, resource, id);
    setRecord(recordRef.current);

    const unsubscribeQueryCache = queryClient.getQueryCache().subscribe(
      notifyManager.batchCalls(() => {
        if (mountedRef.current) {
          recordRef.current = getRecordFromQueryCache(queryClient, resource, id);
          setRecord(recordRef.current);
        }
      })
    );

    const unsubscribeMutationCache = queryClient.getMutationCache().subscribe(
      notifyManager.batchCalls(() => {
        if (mountedRef.current) {
          setRecord(recordRef.current);
        }
      })
    );

    return () => {
      mountedRef.current = false;
      unsubscribeQueryCache();
      unsubscribeMutationCache();
    };
  }, [queryClient, id, resource]);

  return record;
};
